import OrganisationTypeEnum from '@constants/enums/organisation/type';
import UserRoleEnum from '@constants/enums/user/role';
import UserStatusEnum from '@constants/enums/user/status';

import i18n from '@plugins/i18n';

import { Collection, User } from '@/models';

export class SettingsUsersCollection extends Collection {
    static type = 'SettingsUsersCollection';
    static endpoint = User.api.index;
    static model = User;

    static fields() {
        return {
            ...super.fields(),
            organisation_id: this.string(null).nullable(),
            organisation_type: this.string(null).nullable(),
            role_in_organisation: this.string(null).nullable()
        };
    }

    get preset_filters() {
        let filters = {};

        if (this.organisation_id) {
            filters.organisation_id = this.filterOperator('is') + ':' + this.organisation_id;
        }

        if (this.role_in_organisation) {
            filters.role_in_organisation = this.role_in_organisation;
            filters.organisation_id = this.filterOperator('is_not') + ':' + this.role_in_organisation;
        }

        return filters;
    }

    get headers() {
        return [
            this.createHeader('can_login', '', {
                active: false,
                filterable: false,
                mock: faker => faker.datatype.boolean()
            }),
            this.createHeader('name', i18n.t('name'), {
                width: '18rem',
                minWidth: '18rem',
                filterable: true,
                mock: faker => faker.person.fullName()
            }),
            this.createHeader('email', i18n.t('email'), {
                active: false,
                width: '15rem',
                filterable: true
            }),
            this.createHeader('organisation', i18n.t('organisation'), {
                path: 'organisation.name',
                width: '11rem',
                filterable: true,
                active: this.organisation_type !== 'INVESTOR'
            }),
            this.createHeader('organisation_type', i18n.t('organisation_type'), {
                active: false,
                filterable: true,
                transform: data => data?.item?.organisation?.type,
                width: '11rem',
                format: 'enum',
                enum: OrganisationTypeEnum,
                enumA11yKey: 'enums.organisation_type'
            }),
            this.createHeader('roles', i18n.t('role'), {
                format: 'enum',
                enum: UserRoleEnum,
                enumA11yKey: 'enums.user_role',
                multiple: true,
                width: '11rem',
                transform: data => {
                    let roles = [];

                    if (data.item.roles) {
                        data.item.roles.forEach(role => {
                            roles.push(...role.roles);
                        });
                    }

                    return [...new Set([...roles])];
                },
                mock: faker =>
                    faker.helpers
                        .arrayElements(Object.values(UserRoleEnum), { min: 1, max: 3 })
                        .map(role => ({ roles: [role] }))
            }),
            this.createHeader('fca_number', i18n.t('fca_number'), {
                filterable: true,
                width: '11rem',
                active: this.organisation_type !== 'INVESTOR'
            }),
            this.createHeader('status', i18n.t('status'), {
                filterable: true,
                width: '8rem',
                format: 'enum',
                enum: UserStatusEnum,
                enumA11yKey: 'enums.user_status',
                enumTypes: {
                    [UserStatusEnum.ACTIVE]: 'success',
                    [UserStatusEnum.INACTIVE]: 'grey',
                    [UserStatusEnum.SUSPENDED]: 'error',
                    [UserStatusEnum.UNVERIFIED]: 'warning'
                }
            }),
            this.createHeader('control', '', {
                format: 'control',
                width: '8rem',
                align: 'center'
            })
        ];
    }

    get default_headers() {
        return ['name', 'organisation', 'roles', 'fca_number', 'status', 'control'];
    }
}

export default SettingsUsersCollection;
